import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar } from 'lucide-react';
import { CATEGORIAS_AUSENCIAS } from '../../../api/absences';
import AbsenceDetails from './AbsenceDetails';

export default function AbsenceCalendar({ absences, loading, currentUser }) {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [monthAbsences, setMonthAbsences] = useState([]);
  const [selectedAbsence, setSelectedAbsence] = useState(null);

  // Filtrar ausencias del mes actual
  useEffect(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();

    const filtered = absences.filter((abs) => {
      const inicio = new Date(abs.fecha_inicio);
      const fin = new Date(abs.fecha_fin);

      // Incluir si la ausencia cae en el mes actual
      return (
        (inicio.getFullYear() === year && inicio.getMonth() === month) ||
        (fin.getFullYear() === year && fin.getMonth() === month) ||
        (inicio <= new Date(year, month, 1) && fin >= new Date(year, month + 1, 0))
      );
    });

    setMonthAbsences(filtered);
  }, [absences, currentDate]);

  const monthNames = [
    'Enero',
    'Febrero',
    'Marzo',
    'Abril',
    'Mayo',
    'Junio',
    'Julio',
    'Agosto',
    'Septiembre',
    'Octubre',
    'Noviembre',
    'Diciembre'
  ];

  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);

    const days = [];
    const startDay = firstDay.getDay(); // 0 = domingo

    // Días del mes anterior para completar la primera semana
    for (let i = 0; i < startDay; i++) {
      days.push(null);
    }

    // Días del mes actual
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push(new Date(year, month, i));
    }

    return days;
  };

  const getAbsencesForDay = (date) => {
    if (!date) return [];

    return monthAbsences.filter((abs) => {
      const inicio = new Date(abs.fecha_inicio);
      const fin = new Date(abs.fecha_fin);

      // Resetear horas para comparar solo fechas
      inicio.setHours(0, 0, 0, 0);
      fin.setHours(0, 0, 0, 0);
      const checkDate = new Date(date);
      checkDate.setHours(0, 0, 0, 0);

      return checkDate >= inicio && checkDate <= fin;
    });
  };

  const previousMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const days = getDaysInMonth(currentDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header del Calendario */}
      <div className="flex items-center justify-between bg-gradient-to-r from-orange-50 to-pink-50 p-4 rounded-xl border border-orange-200">
        <button
          onClick={previousMonth}
          className="p-2 hover:bg-white rounded-lg transition-colors"
        >
          <ChevronLeft className="w-5 h-5 text-gray-700" />
        </button>

        <div className="text-center">
          <h3 className="text-2xl font-bold text-gray-800">
            {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
          </h3>
          <button
            onClick={goToToday}
            className="text-sm text-orange-600 hover:text-orange-700 font-medium mt-1"
          >
            Hoy
          </button>
        </div>

        <button
          onClick={nextMonth}
          className="p-2 hover:bg-white rounded-lg transition-colors"
        >
          <ChevronRight className="w-5 h-5 text-gray-700" />
        </button>
      </div>

      {/* Leyenda */}
      <div className="flex flex-wrap items-center gap-4 text-sm">
        <span className="font-semibold text-gray-700">Leyenda:</span>
        {Object.entries(CATEGORIAS_AUSENCIAS).slice(0, 5).map(([key, config]) => (
          <div key={key} className="flex items-center gap-2">
            <span className="text-xl">{config.icon}</span>
            <span className="text-gray-600">{config.label}</span>
          </div>
        ))}
      </div>

      {/* Grid del Calendario */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {/* Nombres de los días */}
        <div className="grid grid-cols-7 bg-gray-100 border-b border-gray-200">
          {dayNames.map((day) => (
            <div
              key={day}
              className="text-center py-3 font-semibold text-gray-700 text-sm"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Días del mes */}
        <div className="grid grid-cols-7">
          {days.map((date, index) => {
            const dayAbsences = date ? getAbsencesForDay(date) : [];
            const isToday =
              date &&
              date.toDateString() === new Date().toDateString();
            const isWeekend = date && (date.getDay() === 0 || date.getDay() === 6);

            return (
              <div
                key={index}
                className={`min-h-[120px] border-r border-b border-gray-200 p-2 ${
                  !date ? 'bg-gray-50' : ''
                } ${isWeekend && date ? 'bg-gray-50' : ''} ${
                  isToday ? 'bg-orange-50 ring-2 ring-orange-400 ring-inset' : ''
                }`}
              >
                {date && (
                  <>
                    {/* Número del día */}
                    <div
                      className={`text-sm font-semibold mb-1 ${
                        isToday
                          ? 'text-orange-700'
                          : isWeekend
                          ? 'text-gray-500'
                          : 'text-gray-700'
                      }`}
                    >
                      {date.getDate()}
                    </div>

                    {/* Ausencias del día */}
                    <div className="space-y-1">
                      {dayAbsences.slice(0, 3).map((abs) => {
                        const config =
                          CATEGORIAS_AUSENCIAS[abs.tipo_categoria] ||
                          CATEGORIAS_AUSENCIAS.permiso;

                        return (
                          <div
                            key={abs.id}
                            onClick={() => setSelectedAbsence(abs)}
                            className="text-xs p-1 rounded bg-white border-l-2 hover:shadow-md transition-shadow cursor-pointer hover:opacity-80"
                            style={{ borderColor: config.color }}
                            title={`${abs.empleado_nombre} - ${abs.tipo_nombre}`}
                          >
                            <div className="flex items-center gap-1">
                              <span>{config.icon}</span>
                              <span className="truncate font-medium text-gray-700">
                                {abs.empleado_nombre?.split(' ')[0]}
                              </span>
                            </div>
                          </div>
                        );
                      })}

                      {dayAbsences.length > 3 && (
                        <div className="text-xs text-gray-500 font-medium pl-1">
                          +{dayAbsences.length - 3} más
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Resumen del mes */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-5 border border-blue-200">
        <h4 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <Calendar className="w-5 h-5" />
          Resumen del Mes
        </h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-gray-600">Total de ausencias</p>
            <p className="text-2xl font-bold text-gray-800">{monthAbsences.length}</p>
          </div>
          <div>
            <p className="text-gray-600">Aprobadas</p>
            <p className="text-2xl font-bold text-green-600">
              {monthAbsences.filter((a) => a.estado === 'aprobado').length}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Pendientes</p>
            <p className="text-2xl font-bold text-orange-600">
              {monthAbsences.filter((a) => a.estado === 'pendiente').length}
            </p>
          </div>
          <div>
            <p className="text-gray-600">Rechazadas</p>
            <p className="text-2xl font-bold text-red-600">
              {monthAbsences.filter((a) => a.estado === 'rechazado').length}
            </p>
          </div>
        </div>
      </div>

      {/* Modal de detalles de ausencia */}
      {selectedAbsence && (
        <AbsenceDetails
          absence={selectedAbsence}
          onClose={() => setSelectedAbsence(null)}
        />
      )}
    </div>
  );
}
